const ECHARTS_URL = "https://cdn.jsdelivr.net/npm/echarts@5.6.0/dist/echarts.min.js"

type Cell = string | number | boolean | null
export type DashboardPdfTable = {
  datasetID: string
  name: string
  headers: string[]
  rows: Array<Record<string, Cell>>
}
export type DashboardPdfSettings = {
  orientation: "portrait" | "landscape"
  margin: number
  scale: number
  scope: "active" | "all"
  allRows: boolean
  density: "original" | "compact" | "normal" | "large"
  fontSize: "compact" | "medium" | "large"
  gray: boolean
  accent: string
  widgets: Record<string, Record<string, Record<string, unknown>>>
}

type PdfWidget = {
  id?: string
  type?: string
  title?: string
  text?: string
  layout?: { x?: number; y?: number; w?: number; h?: number }
  data?: Record<string, unknown>
  style?: Record<string, unknown>
}
type PdfTab = { id?: string; name?: string; widgets?: PdfWidget[] }
type PdfConfig = {
  title?: string
  tabs?: PdfTab[]
  design?: Record<string, unknown>
  canvas?: { width?: number; height?: number }
}

export function serializeDashboardPdf(input: {
  config: PdfConfig
  tables: DashboardPdfTable[]
  activeTabID?: string
  settings: DashboardPdfSettings
  dashboardTitle: string
}) {
  const tabs = (input.config.tabs ?? []).filter(
    (tab) => input.settings.scope === "all" || tab.id === input.activeTabID,
  )
  const selectedTabs = tabs.length ? tabs : (input.config.tabs ?? []).slice(0, 1)
  const accent = safeColor(input.settings.accent, "#2f80ed")
  const headerColor = safeColor(input.config.design?.headerColor, "#101828")
  const background = input.settings.gray ? "#f8fafc" : "#ffffff"
  const charts: Array<{ id: string; option: Record<string, unknown> }> = []
  const canvasWidth = Math.max(1, number(input.config.canvas?.width) || (input.settings.orientation === "portrait" ? 794 : 1123))
  const canvasHeight = Math.max(1, number(input.config.canvas?.height) || (input.settings.orientation === "portrait" ? 1123 : 794))
  const pages = selectedTabs.map((tab, tabIndex) => {
    const filters = (tab.widgets ?? []).filter((widget) => widget.type === "filter" && selectedValues(widget).length)
    const widgets = [...(tab.widgets ?? [])]
      .sort((first, second) => number(first.layout?.y) - number(second.layout?.y) || number(first.layout?.x) - number(second.layout?.x))
      .map((widget, widgetIndex) => widgetHtml(widget, tab, tabIndex, widgetIndex, input.tables, filters, input.settings, charts, accent, headerColor, canvasWidth, canvasHeight))
      .join("")
    const completeTables = input.settings.allRows
      ? (tab.widgets ?? []).filter((widget) => ["table", "freeTable"].includes(widget.type ?? "")).map((widget) => {
          const table = prepareTable(findTable(input.tables, widget), widget, filters, false)
          return `<section class="complete-table page-break"><h2>${escapeHtml(tab.name || input.dashboardTitle)}: ${escapeHtml(widget.title || table?.name || "Table")}</h2>${tableHtml(table, widget, input.settings.density, Infinity)}</section>`
        }).join("")
      : ""
    return `<section class="dashboard-page"><div class="widget-grid" style="aspect-ratio:${canvasWidth}/${canvasHeight}">${widgets}</div></section>${completeTables}`
  }).join("")
  const title = input.config.title || input.dashboardTitle
  const margin = clamp(input.settings.margin, 0, 30)
  const baseFont = input.settings.fontSize === "compact" ? 10 : input.settings.fontSize === "large" ? 14 : 12
  const scale = clamp(input.settings.scale, 50, 100) / 100
  const chartScript = charts.length
    ? `<script src="${ECHARTS_URL}"></script><script>const charts=${safeJson(charts)};for(const chart of charts){const node=document.getElementById(chart.id);if(node){const instance=echarts.init(node,null,{renderer:"svg"});instance.setOption(chart.option,{notMerge:true});}}</script>`
    : ""

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${escapeHtml(title)}</title><style>
@page{size:A4 ${input.settings.orientation};margin:${margin}mm}*{box-sizing:border-box}html{color:#101828;background:${background};font-family:Arial,sans-serif;font-size:${baseFont * scale}px}body{margin:0;background:${background}}h1,h2,h3,p{margin-top:0}h1{font-size:2em;margin-bottom:18px}h2{font-size:1.45em}h3{font-size:1.1em}.eyebrow{color:${accent};font-weight:700;text-transform:uppercase;letter-spacing:.08em}.dashboard-page{break-after:page;min-height:170mm}.dashboard-page:last-child{break-after:auto}.widget-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:10px}.widget{grid-column:span 6;border:1px solid #d0d5dd;border-radius:10px;padding:12px;break-inside:avoid;background:#fff}.widget.header,.widget.title,.widget.text{grid-column:1/-1}.widget.header{color:#fff;background:${accent};border:0}.widget.table{grid-column:1/-1}.widget-content{min-height:24px}.chart{height:230px;width:100%}.kpi-value{font-size:2.4em;font-weight:700;color:${accent}}table{border-collapse:collapse;width:100%;font-size:.92em}thead{display:table-header-group}tr{break-inside:avoid}th,td{border:1px solid #d0d5dd;padding:6px 7px;text-align:left;vertical-align:top;white-space:normal;overflow-wrap:anywhere}th{background:#eef2f6;font-weight:700}.striped tbody tr:nth-child(even){background:#f8fafc}.wide-table{font-size:.72em;table-layout:fixed}.wide-table th,.wide-table td{padding:4px}.density-compact th,.density-compact td{padding:3px 5px}.density-large th,.density-large td{padding:9px}.page-break{break-before:page}.complete-table{break-after:auto}.empty{color:#667085;font-style:italic}.filter-values{display:flex;gap:6px;flex-wrap:wrap}.filter-values span{border:1px solid #d0d5dd;border-radius:999px;padding:3px 8px}@media print{.widget{box-shadow:none}.chart{height:55mm}}
.widget-grid{position:relative!important;display:block!important;width:100%}.widget{position:absolute!important;overflow:hidden}.widget.header{left:calc(-1 * ${margin}mm)!important;width:calc(100% + ${margin * 2}mm)!important;background:${headerColor}!important;border-radius:0}.widget.kpi{display:flex;min-width:0;flex-direction:column}.widget.kpi h2{max-height:2.4em;margin:0 0 6px;overflow:hidden;overflow-wrap:anywhere;line-height:1.2}.widget.kpi .kpi-value{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;line-height:1.1}.chart{height:100%;min-height:0}.pdf-editing .widget{cursor:move;outline:1px solid transparent}.pdf-editing .widget:hover{outline-color:${accent}}.pdf-editing .widget:after{content:"";position:absolute;right:2px;bottom:2px;width:10px;height:10px;border-right:2px solid ${accent};border-bottom:2px solid ${accent};cursor:nwse-resize}
</style></head><body><main>${pages}</main>${chartScript}</body></html>`
}

function widgetHtml(widget: PdfWidget, tab: PdfTab, tabIndex: number, widgetIndex: number, tables: DashboardPdfTable[], filters: PdfWidget[], settings: DashboardPdfSettings, charts: Array<{ id: string; option: Record<string, unknown> }>, accent: string, headerColor: string, canvasWidth: number, canvasHeight: number) {
  const type = widget.type || "text"
  const title = widget.title || ""
  const overrides = settings.widgets[tab.id || ""]?.[widget.id || ""] ?? {}
  const titleStyle = Number.isFinite(Number(overrides.titleSize)) ? ` style="font-size:${clamp(Number(overrides.titleSize), 8, 64)}px"` : ""
  const contentStyle = Number.isFinite(Number(overrides.contentSize)) ? ` style="font-size:${clamp(Number(overrides.contentSize), 8, 32)}px"` : ""
  const requestedLayout = isRecord(overrides.layout) ? overrides.layout : widget.layout
  const effectiveLayout = type === "header" ? { ...requestedLayout, x: 0, w: canvasWidth } : requestedLayout
  const layout = widgetLayoutAttribute(widget, effectiveLayout, canvasWidth, canvasHeight)
  const identity = ` data-tab-id="${escapeAttribute(tab.id || "")}" data-widget-id="${escapeAttribute(widget.id || "")}"`
  const table = prepareTable(findTable(tables, widget), widget, filters, true)
  if (type === "header") return `<header class="widget header"${identity}${layout}><h2${titleStyle}>${escapeHtml(title)}</h2></header>`
  if (type === "title") return `<section class="widget title"${identity}${layout}><h2${titleStyle}>${escapeHtml(title)}</h2></section>`
  if (type === "text") return `<section class="widget text"${identity}${layout}><p${contentStyle}>${escapeHtml(widget.text || "")}</p></section>`
  if (["table", "freeTable"].includes(type)) return `<section class="widget table"${identity}${layout}><h2${titleStyle}>${escapeHtml(title || table?.name || "Table")}</h2>${tableHtml(table, widget, widget.style?.density as DashboardPdfSettings["density"], Number(widget.data?.rowLimit) || 20)}</section>`
  if (type === "filter") return `<section class="widget filter"${identity}${layout}><h2${titleStyle}>${escapeHtml(title)}</h2><div class="filter-values">${selectedValues(widget).map((value) => `<span>${escapeHtml(value)}</span>`).join("") || '<span class="empty">All</span>'}</div></section>`
  if (type === "kpi") {
    const values = (table?.rows ?? []).map((row) => numeric(row[String(widget.data?.metric || "")])).filter((value): value is number => value !== null)
    const aggregate = String(widget.data?.aggregate || "sum")
    const value = aggregate === "count" ? (table?.rows.length ?? 0) : aggregate === "average" ? values.reduce((sum, item) => sum + item, 0) / (values.length || 1) : aggregate === "min" ? Math.min(...values) : aggregate === "max" ? Math.max(...values) : values.reduce((sum, item) => sum + item, 0)
    const valueScale = clamp(Number(overrides.valueScale ?? 100), 25, 100) / 100
    return `<section class="widget kpi"${identity}${layout}><h2${titleStyle}>${escapeHtml(title)}</h2><div class="kpi-value" style="font-size:${2.4 * valueScale}em">${escapeHtml(Number.isFinite(value) ? new Intl.NumberFormat("en").format(value) : "-")}</div></section>`
  }
  const id = `chart-${tabIndex}-${widgetIndex}`
  charts.push({ id, option: chartOption(widget, table, accent) })
  return `<figure class="widget"${identity}${layout}><figcaption${contentStyle}><h2${titleStyle}>${escapeHtml(title)}</h2></figcaption><div class="chart" id="${id}" role="img" aria-label="${escapeAttribute(title)}"></div></figure>`
}

function widgetLayoutAttribute(widget: PdfWidget, layout: Record<string, unknown> | PdfWidget["layout"], width: number, height: number) {
  if (!layout) return ""
  return ` style="left:${clamp(number(layout.x) / width * 100, 0, 100)}%;top:${clamp(number(layout.y) / height * 100, 0, 100)}%;width:${clamp(number(layout.w) / width * 100, 0, 100)}%;height:${clamp(number(layout.h) / height * 100, 0, 100)}%"`
}

function tableHtml(table: DashboardPdfTable | undefined, widget: PdfWidget, density: DashboardPdfSettings["density"] | undefined, limit: number) {
  if (!table) return '<p class="empty">No data</p>'
  const headers = table.headers
  const shown = table.rows.slice(0, limit)
  const densityClass = ["compact", "normal", "large"].includes(density ?? "") ? `density-${density}` : ""
  const classes = [headers.length > 8 ? "wide-table" : "", widget.style?.stripedRows ? "striped" : "", densityClass].filter(Boolean).join(" ")
  const head = widget.style?.showHeader === false ? "" : `<thead><tr>${headers.map((header) => `<th scope="col">${escapeHtml(header)}</th>`).join("")}</tr></thead>`
  const rows = shown.map((row) => `<tr>${headers.map((header) => `<td>${escapeHtml(row[header] ?? "")}</td>`).join("")}</tr>`).join("")
  return `<table class="${classes}">${head}<tbody>${rows}</tbody></table>`
}

function prepareTable(table: DashboardPdfTable | undefined, widget: PdfWidget, filterWidgets: PdfWidget[], sample: boolean) {
  if (!table) return undefined
  const targets = filterWidgets.flatMap((filter) => {
    const target = Array.isArray(filter.data?.targets) ? filter.data.targets.find((item) => isRecord(item) && item.dataset_id === widget.data?.dataset_id) : undefined
    return isRecord(target) && target.column ? [{ column: String(target.column), values: selectedValues(filter) }] : []
  })
  const localFilters = Array.isArray(widget.data?.filters) ? widget.data.filters.filter(isRecord) : []
  const rows = table.rows.filter((row) => targets.every((filter) => filter.values.includes(String(row[filter.column] ?? ""))))
    .filter((row) => localFilters.every((filter) => rowMatchesFilter(row, filter)))
  const sort = isRecord(widget.data?.sort) ? widget.data.sort : undefined
  const sorted = !sort?.column ? rows : [...rows].sort((first, second) => {
    const left = first[String(sort.column)]
    const right = second[String(sort.column)]
    const numericResult = numeric(left) !== null && numeric(right) !== null ? numeric(left)! - numeric(right)! : String(left ?? "").localeCompare(String(right ?? ""))
    return sort.direction === "desc" ? -numericResult : numericResult
  })
  const requestedHeaders = Array.isArray(widget.data?.columns) ? widget.data.columns.map(String).filter((header) => table.headers.includes(header)) : []
  const rowLimit = sample && Number(widget.data?.rowLimit) > 0 ? Number(widget.data?.rowLimit) : undefined
  return { ...table, headers: requestedHeaders.length ? requestedHeaders : table.headers, rows: rowLimit ? sorted.slice(0, rowLimit) : sorted }
}

function chartOption(widget: PdfWidget, table: DashboardPdfTable | undefined, accent: string): Record<string, unknown> {
  const data = widget.data ?? {}
  const label = String(data.label || table?.headers[0] || "")
  const measures = Array.isArray(data.measures) && data.measures.length ? data.measures.map(String) : [String(data.metric || table?.headers[1] || "")]
  const rows = (table?.rows ?? []).slice(0, Number(data.showTop) || 12)
  const names = rows.map((row, index) => String(row[label] ?? `Item ${index + 1}`))
  if (["pie", "funnel", "treemap"].includes(widget.type || "")) return {
    animation: false,
    tooltip: {},
    series: [{ type: widget.type, radius: widget.type === "pie" ? ["35%", "68%"] : undefined, data: rows.map((row, index) => ({ name: names[index], value: numeric(row[measures[0]]) ?? 0 })) }],
  }
  if (widget.type === "scatter") return {
    animation: false,
    xAxis: {}, yAxis: {}, tooltip: {},
    series: [{ type: "scatter", data: rows.map((row) => [numeric(row[measures[0]]) ?? 0, numeric(row[measures[1] || measures[0]]) ?? 0]), itemStyle: { color: accent } }],
  }
  return {
    animation: false,
    color: [accent, "#7048e8", "#339989", "#d6334c"],
    tooltip: {}, legend: measures.length > 1 ? {} : { show: false },
    grid: { left: 12, right: 12, top: measures.length > 1 ? 42 : 20, bottom: 20, containLabel: true },
    xAxis: { type: "category", data: names, axisLabel: { interval: 0, rotate: names.some((name) => name.length > 12) ? 30 : 0 } },
    yAxis: { type: "value" },
    series: measures.map((measure) => ({ name: measure, type: widget.type === "line" || widget.type === "multiSeries" ? "line" : "bar", data: rows.map((row) => numeric(row[measure]) ?? 0) })),
  }
}

function findTable(tables: DashboardPdfTable[], widget: PdfWidget) {
  return tables.find((table) => table.datasetID === widget.data?.dataset_id)
}

function selectedValues(widget: PdfWidget) {
  return Array.isArray(widget.data?.selectedValues) ? widget.data.selectedValues.map(String) : []
}

function rowMatchesFilter(row: Record<string, Cell>, filter: Record<string, unknown>) {
  if (!filter.column) return true
  const value = String(row[String(filter.column)] ?? "")
  if (filter.operator === "notEmpty") return value.trim().length > 0
  if (filter.operator === "equals") return value.toLowerCase() === String(filter.value ?? "").toLowerCase()
  return value.toLowerCase().includes(String(filter.value ?? "").toLowerCase())
}

function escapeHtml(value: unknown) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!)
}

function escapeAttribute(value: unknown) {
  return escapeHtml(value).replace(/\r|\n/g, " ")
}

function safeJson(value: unknown) {
  return JSON.stringify(value).replace(/[<>&\u2028\u2029]/g, (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`)
}

function safeColor(value: unknown, fallback: string) {
  return typeof value === "string" && /^(#[0-9a-f]{3,8}|[a-z]{3,20})$/i.test(value) ? value : fallback
}

function numeric(value: unknown) {
  const parsed = typeof value === "number" ? value : Number(String(value ?? "").replace(/[\s,]/g, ""))
  return Number.isFinite(parsed) ? parsed : null
}

function number(value: unknown) {
  return Number.isFinite(Number(value)) ? Number(value) : 0
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}
